// The FOCUSED SESSION — one source of truth for "the page is emptied" (busy-my-web#255).
//
// The box already knew all of this; the trouble was that only the box knew it. Content.vue learned
// the creation mode through an `emit`, the sidebar guessed at it from `research/draftEntity`, and
// the rail did not know at all — which is why hiding the Follow-ups for a task (#183) never reached
// the other four modules, never reached an edit, and never reached the Ongoing list.
//
// So the SmartBox writes what it is doing here, once, and the four surfaces that have to step
// aside — the Follow-ups/Focus strip, the home greeting, the Ongoing list and the Timeline rail —
// read it. The rules themselves stay pure: `isFocusedSession` in lib/creationCapabilities.js (the
// same gate #253 asks about the web search) and the trimming in lib/focusedPage.js.

import { isCreationSession, isFocusedSession } from '@/lib/creationCapabilities'
import { focusedModule, focusedTimelineItem, focusedOngoingRows } from '@/lib/focusedPage'
import { ongoingLink } from '@/lib/threadOngoing'
import api from '@/lib/services/general.service'

export const state = {
  // The in-box quick mode: 'tasks' | 'notes' | 'events' | 'emails' | 'contacts' | null.
  quickMode: null,
  // The full creation form, and which module it is set to.
  formMode: false,
  formMenu: null,
  // The entity open in the box: { menu, id, ongoingId, title } — null when nothing is.
  editing: null,
  // The box is a reply / forward to an open thread (busy-my-web#480): the page is NOT emptied
  // for it — lib/creationCapabilities.js's one exemption.
  replying: false,
  // The entity opened IN PLACE in its own listing (busy-my-web#483): { menu, id, ongoingId, title }.
  //
  // Its sibling `editing` means "open in the BOX", which is a focused session — the page empties
  // for it (#255). An inline expand in the Tasks/Notes/Events/Contacts list is not that: the list
  // stays, the other rows stay, nothing steps aside. The ONE thing the frames give it
  // (40002677:60186 → 40004745:121647) is the right rail: it becomes that entity's own Timeline,
  // with the named way back to the Global timeline. So it is a second, quieter reference — read by
  // the rail, ignored by `isFocusedSession`.
  opened: null,
  // The Ongoings the item open in a READER belongs to (busy-my-web#617) — the left list trims to
  // them (lib/focusedPage.js `openedOngoingRows`). Read from the graph by the open item's timeline,
  // exactly as the email reader reads "Part of <story>" (components/Email/ThreadReader.vue).
  // `ids` is null while unknown (not read yet, or the read failed) — the list then stays whole.
  openedOngoings: { timelineId: null, ids: null, loading: false },
}

export const getters = {
  // Ticking results in a web search is the third focused state, and it is not the box's to report:
  // it is the workboard's selection during a research. Derived here so the flag has one shape
  // whoever asks, and so the comparison flow keeps owning its own surfaces.
  selecting: (s, g, rootState, rootGetters) =>
    !!rootGetters['research/active'] && (rootGetters['workboard/count'] || 0) > 0,

  /** The whole session, in the shape the pure rules take. */
  session: (s, g) => ({
    quickMode: s.quickMode,
    formMode: s.formMode,
    formMenu: s.formMenu,
    editing: s.editing,
    selecting: g.selecting,
    replying: s.replying,
    opened: s.opened,
  }),

  /** Is the box a reply / forward right now? (busy-my-web#480) */
  replying: (s) => s.replying,

  /**
   * The entity open in the box, or — failing that — the one expanded in its own listing
   * (busy-my-web#483). THE reference the right rail follows: both are "an entity the user is
   * looking at", and the rail is about the thing being looked at, not about which surface opened
   * it. The box wins where both are set, because the box is the nearer of the two.
   */
  railEntity: (s) => s.editing || s.opened,

  /** Is the page emptied right now? THE flag. */
  active: (s, g) => isFocusedSession(g.session),

  /** …and is it emptied for something being MADE (as opposed to edited or ticked)? */
  creating: (s, g) => isCreationSession(g.session) && !s.editing,

  /** The module in play — 'task' | 'email' | 'event' | 'note' | 'contact' | 'research' | ''. */
  module: (s, g) => focusedModule(g.session),

  /**
   * The one item the Timeline shows while something is being created; null otherwise.
   *
   * It carries the files ATTACHED to the thing being made (busy-my-web#444) — "Ready when you are /
   * 2 items selected" over their rows. Those live in their own module (`composerFiles`) rather than
   * in this state: this one is the SESSION — what the box is doing — and the selection changes many
   * times inside one session.
   */
  timelineItem: (s, g, rootState) => focusedTimelineItem(g.session, rootState.composerFiles?.files),

  /** `{ timelineId, ids, loading }` — the open reader item's Ongoings (busy-my-web#617). */
  openedOngoings: (s) => s.openedOngoings,

  /** The Ongoing list, trimmed to the ongoing being worked in (untouched when not focused). */
  ongoingRows: (s, g) => (rows) => focusedOngoingRows(rows, g.session),
}

export const mutations = {
  SET_QUICK_MODE(s, mode) { s.quickMode = mode || null },
  SET_FORM(s, { open, menu } = {}) { s.formMode = !!open; s.formMenu = open ? (menu || null) : null },
  SET_EDITING(s, editing) { s.editing = editing || null },
  SET_REPLYING(s, on) { s.replying = !!on },
  SET_OPENED(s, opened) { s.opened = opened || null },
  SET_OPENED_ONGOINGS(s, v) {
    s.openedOngoings = { timelineId: v?.timelineId || null, ids: Array.isArray(v?.ids) ? v.ids : null, loading: !!v?.loading }
  },
  // The title arrives after the entity does (the box seeds its fields a tick later), so it patches
  // rather than replaces — replacing would drop the menu/ongoingId the trimming reads.
  PATCH_EDITING_TITLE(s, title) { if (s.editing) s.editing = { ...s.editing, title: title || '' } },
}

export const actions = {
  setQuickMode({ commit }, mode) { commit('SET_QUICK_MODE', mode) },
  setForm({ commit }, payload) { commit('SET_FORM', payload) },
  setEditing({ commit }, editing) {
    commit('SET_EDITING', editing?.menu
      ? { menu: editing.menu, id: editing.id || null, ongoingId: editing.ongoingId || null, title: editing.title || '' }
      : null)
  },
  setEditingTitle({ commit }, title) { commit('PATCH_EDITING_TITLE', title) },
  setReplying({ commit }, on) { commit('SET_REPLYING', on) },
  /** An entity expanded in its own listing — null when it collapses again (busy-my-web#483). */
  setOpened({ commit }, opened) {
    commit('SET_OPENED', opened?.menu
      ? { menu: opened.menu, id: opened.id || null, ongoingId: opened.ongoingId || null, title: opened.title || '' }
      : null)
    // Nothing open → nothing to trim the list to (busy-my-web#617): the full list comes back.
    if (!opened?.menu) commit('SET_OPENED_ONGOINGS', null)
  },
  /**
   * Read which Ongoings contain the open reader item's timeline (busy-my-web#617). The readers call
   * it where they publish `opened`. Asking again for the timeline already read (or being read) is a
   * no-op; a failed read leaves `ids` null — unknown, so the list stays whole rather than claiming
   * the item is in no story. An answer for a timeline that is no longer the open one is dropped.
   */
  async loadOpenedOngoings({ commit, state: s }, timelineId) {
    const id = timelineId ? String(timelineId) : null
    if (!id) { commit('SET_OPENED_ONGOINGS', null); return }
    const cur = s.openedOngoings
    if (cur.timelineId === id && (cur.loading || Array.isArray(cur.ids))) return
    commit('SET_OPENED_ONGOINGS', { timelineId: id, ids: null, loading: true })
    let ids = null
    try {
      const resolved = (await api.getObject('timeline', id))?.data
      // ONE Ongoing, never a list (Pouria, 2026-10-09, #617 follow-up: "one task is only related to
      // one ongoing and belongs to one ongoing, it should always be one"). The graph may hold more
      // than one inbound edge — the task's own story plus a SUGGESTED relation to the research
      // Ongoing it was created under — and listing them all showed two. `ongoingLink` is the email
      // reader's pick for "Part of <story>": confirmed beats suggested, newest wins.
      const link = ongoingLink(resolved)
      ids = link ? [String(link.id)] : []
    } catch {
      ids = null
    }
    if (s.openedOngoings.timelineId !== id) return
    commit('SET_OPENED_ONGOINGS', { timelineId: id, ids, loading: false })
  },
  /** Everything at once — what leaving the state (save / cancel / close) restores. */
  clear({ commit }) {
    commit('SET_QUICK_MODE', null)
    commit('SET_FORM', { open: false })
    commit('SET_EDITING', null)
    commit('SET_REPLYING', false)
    commit('SET_OPENED', null)
    commit('SET_OPENED_ONGOINGS', null)
  },
}
